/** G1 屏共用的小件：分区取数外壳、链路五步卡、事件表。 */

import type { ReactNode } from "react";

import { Card, Note } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { clock, ts } from "../../lib/format";
import { CHAIN_RERUN_PATH, chainRerunExtra, lastManualRunText } from "../../lib/chainRerun";
import { bridgeOutcomeView, describeEvent, shortUtc, stepView, type DailyChain, type EventRow } from "../../lib/g1";
import { ActionForm } from "../p2/Common";

/** 分区级取数外壳：只这一个分区报错 / 重试，其它分区照常（规格「单个分区网络错误」）。 */
export function Load<T>({
  q,
  what,
  children,
}: {
  q: { status: "pending" | "error" | "success"; error: unknown; data: T | undefined; refetch: () => unknown };
  what: string;
  children: (data: T) => ReactNode;
}): ReactNode {
  return (
    <QueryState status={q.status} error={q.error} what={what} refetch={() => void q.refetch()}>
      {q.status === "success" ? children(q.data as T) : null}
    </QueryState>
  );
}

export function ChainTable({ chain }: { chain: DailyChain }): ReactNode {
  const rows = chain.steps.flatMap((step, index) => {
    const view = stepView(step.status);
    const main = {
      key: step.step,
      cls: step.status === "missing" || step.status === "failed" ? "bad" : "",
      cells: [
        `${index + 1}. ${step.label}`,
        <Pill tone={view.tone}>{view.text}</Pill>,
        shortUtc(step.last_ts_utc),
        <span>
          {step.detail}
          {step.step === "mirror_sync" && step.lag_days !== null ? (
            <span className="faint">
              {" "}
              · 落后 {step.lag_days} 个交易日{step.missing_days.length ? `（漏 ${step.missing_days.join("、")}）` : ""}
            </span>
          ) : null}
        </span>,
      ],
    };
    const lines = step.per_line.map((line) => {
      const v = bridgeOutcomeView(line.outcome);
      return {
        key: `${step.step}-${line.source}`,
        cells: [
          <span className="faint">　└ {line.source}</span>,
          <Pill tone={v.tone}>{v.text}</Pill>,
          "",
          `受理 ${line.accepted} · 拒收 ${line.rejected} · 不下单 ${line.no_order}${line.cadence_days ? ` · 节奏 ${line.cadence_days} 天` : ""} · ${line.detail}`,
        ],
      };
    });
    return [main, ...lines];
  });
  return (
    <>
      {!chain.is_trading_day ? (
        <Note>{chain.requested_date} 不是交易日，下面是上一个交易日 {chain.trade_date} 的五步。</Note>
      ) : null}
      <Table
        cols={[{ label: "步骤", width: "18%" }, { label: "结果", width: "12%" }, { label: "最近一次", width: "14%" }, { label: "说明", wrap: true }]}
        rows={rows}
      />
      {chain.complete ? null : (
        <Note tone={chain.missing_steps.length ? "bad" : "warn"}>
          当日链路不完整
          {chain.missing_steps.length
            ? `：${chain.steps.filter((s) => chain.missing_steps.includes(s.step)).map((s) => s.label).join("、")} 未完成`
            : "（有步骤未到点或无从判定）"}
          。补跑入口：快照在「数据快照」、桥接在「信号收件箱」、跑一天在「运行与回放」、日报在「每日报告」。
        </Note>
      )}
    </>
  );
}

export function ChainCard({ chain, halted }: { chain: DailyChain; halted: boolean }): ReactNode {
  return (
    <Card title={`每日链路 · ${chain.trade_date}`} sub="GET /chain/daily · 服务端判定" nopad>
      <ChainTable chain={chain} />
      {chain.last_manual_run ? (
        <Note tone={chain.last_manual_run.broken_step || chain.last_manual_run.job_status === "failed" ? "bad" : undefined}>
          最近一次手工重跑：{lastManualRunText(chain.last_manual_run)}
        </Note>
      ) : null}
      <ChainActions tradeDate={chain.trade_date} halted={halted} />
    </Card>
  );
}

/** 重跑链路 / 从断点续跑（E4 #5007，`POST /chain/daily/run`）：五步怎么跑、断点在哪、停机拒哪一步都由服务端判定。 */
export function ChainActions({ tradeDate, halted }: { tradeDate: string; halted: boolean }): ReactNode {
  const fields = [
    { name: "operator", label: "操作人" },
    { name: "reason", label: "理由" },
  ];
  const haltNote = halted ? "停机中：跑一天一步会被服务端拒（其余步照跑）。" : null;
  return (
    <div className="grid g2">
      <ActionForm
        path={CHAIN_RERUN_PATH}
        title="重跑链路"
        fields={fields}
        extra={chainRerunExtra(tradeDate, false)}
        note={<>按五步整轮重跑 {tradeDate}（与定时任务同一编排）。{haltNote}</>}
      />
      <ActionForm
        path={CHAIN_RERUN_PATH}
        title="从断点续跑"
        fields={fields}
        extra={chainRerunExtra(tradeDate, true)}
        note={<>从 {tradeDate} 第一个未执行 / 失败的步续跑，断点由服务端判定。{haltNote}</>}
      />
    </div>
  );
}

export function EventTable({ rows, empty }: { rows: EventRow[]; empty: ReactNode }): ReactNode {
  return (
    <Table
      cols={[{ label: "#", num: true, width: "6%" }, { label: "时间", width: "16%" }, { label: "级别", width: "8%" }, { label: "来源", width: "8%" }, { label: "类型", width: "8%" }, { label: "内容", wrap: true }]}
      rows={rows.map((row) =>
        row.kind === "gap"
          ? { key: `gap-${row.seq}`, cls: "warn", cells: ["", "", "", "", "", `此处缺 ${row.missing ?? "?"} 条事件（超出缓冲 1000 条）`] }
          : {
              key: String(row.seq),
              cls: row.event?.type === "halt" || row.event?.level === "ERROR" ? "bad" : "",
              cells: [
                row.seq,
                <span title={ts(row.event?.ts_ns)}>{ts(row.event?.ts_ns).slice(5, 10)} {clock(row.event?.ts_ns)}</span>,
                <Pill tone={row.event?.level === "ERROR" ? "bad" : row.event?.level === "WARN" ? "warn" : "neutral"}>{row.event?.level}</Pill>,
                row.event?.source,
                row.event?.type,
                row.event ? describeEvent(row.event) : "",
              ],
            },
      )}
      empty={empty}
    />
  );
}
